import {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';

import Button from '../../../components/button';
import ChatHeaderIcon from '../../../components/mazraeKit/headers/ChatHeaderIcon/ChatHeaderIcon';
import CustomHeader from '../../../components/mazraeKit/headers/homeHeader';
import NotificationHeaderIcon from '../../../components/mazraeKit/headers/NotificationHeaderIcon/NotificationHeaderIcon';
import NoItemFoundPage from '../../../components/noItemFoundPage';
import PageLoader from '../../../components/pageLoader';
import StickySection from '../../../components/StickySection/StickySection';

import useGetAllListLoanRequest from '../../../services/hookService/getAllListLoanRequest/useGetAllListLoanRequest';
import useGetListTakenLoan from '../../../services/hookService/getListTakenLoan/useGetListTakenLoan';

import {navigateToStep} from '../../lendtech/helpers/navigateToStep';

import InProgressLoanCard from './components/InProgressLoanCard';
import LoanTabSelector from './components/LoanTabSelector';
import TakenLoanCard from './components/TakenLoanCard';

import MainFooter from '../../../components/mazraeKit/footers/footerMain';

import {
  CashBackSource,
  CashBackStatus,
  ICashBackCard,
} from '../../lendtech/cashBack/api/getCashBackCards/cashBackCards.types';
import useGetCashBackCards from '../../lendtech/cashBack/api/getCashBackCards/useGetCashBackCards';
import CashBackCard from './components/CashBackCard';
import DefaultCashBackSheet from './components/DefaultCashBackSheet';
import {LoanRequestStatus} from './consts/loanStatus';
import getCurrentTabState, {
  CurrentTabStateTypes,
} from './helper/getCurrentTabState';
import getLoanPageHeader from './helper/getLoanPageHeader';
import useCollapseOnScroll from '../../../utilities/hooks/useScrollDown/useScrollDown';

export const DEFAULT_CASH_BACK: ICashBackCard = {
  guid: 'default',
  amount: 0,
  reserved_amount: 0,
  used_amount: 0,
  source: CashBackSource.Admin,
  status: CashBackStatus.Inactive,
};

export type MyCreditsTabsType = 'current' | 'paid';

type CreditItem =
  | {
      type: 'loan-request';
      data: ILoanRequestItem;
    }
  | {
      type: 'taken-loan';
      data: ITakenLoan;
    }
  | {
      type: 'cashback';
      data: ICashBackCard;
    };

const TAB_CURRENT: MyCreditsTabsType = 'current'; // جاری
const TAB_PAID: MyCreditsTabsType = 'paid'; // تسویه شده

const isCompletedLoanRequest = (loan: ILoanRequestItem) => {
  return loan?.status === LoanRequestStatus.COMPLETED;
};

const isPaidLoanRequest = (loan: ILoanRequestItem) => {
  return (
    isCompletedLoanRequest(loan) &&
    loan?.is_direct === true &&
    loan?.is_paid_in_bank === true
  );
};

const MyCreditsPage = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const location = useLocation();

  const locationState = location.state as
    | {
        hasLoanRequest?: boolean;
      }
    | undefined;

  const searchParams = new URLSearchParams(location.search);

  const tabParam = searchParams.get('tab');

  // URL is the single source of truth
  const selectedTab: MyCreditsTabsType =
    tabParam === TAB_PAID ? TAB_PAID : TAB_CURRENT;

  const [selectedLoanGuid, setSelectedLoanGuid] = useState<string>('');
  const [openDefaultCashBackSheet, setOpenDefaultCashBackSheet] =
    useState<boolean>(false);

  // While the list is scrolled down, the tab selector, header text and action
  // buttons are removed from the DOM so the list can use their space.
  // They come back on scroll up, at the top, or near the end of the list.
  const {scrollRef, isCollapsed, expand} = useCollapseOnScroll({
    resetKey: selectedTab,
  });

  // Auto expand when user clicks on card
  useEffect(() => {
    if (isCollapsed && selectedLoanGuid) expand();
  }, [selectedLoanGuid]);

  // Ensure valid tab exists
  useEffect(() => {
    if (tabParam !== TAB_CURRENT && tabParam !== TAB_PAID) {
      const params = new URLSearchParams(location.search);

      params.set('tab', TAB_CURRENT);

      navigate(`${location.pathname}?${params.toString()}`, {
        replace: true,
      });
    }
  }, [location.pathname, location.search, navigate, tabParam]);

  const {
    data: allCashBacks,
    currentCashBacks,
    paidCashBacks,
    isLoading: isCashBackCardsLoading,
  } = useGetCashBackCards();

  // Loan requests
  const {data: loanRequests = [], isLoading: isLoanRequestsLoading} =
    useGetAllListLoanRequest({}, 'no-cache');

  // Taken loans
  const {data: takenLoans = [], isLoading: isTakenLoansLoading} =
    useGetListTakenLoan();

  // Toast after redirect
  useEffect(() => {
    if (!locationState?.hasLoanRequest) return;

    toast.error('شما درخواست وام یا درخواست اعتبار در حال پردازش دارید.');

    navigate(location.pathname, {
      replace: true,
      state: {},
    });
  }, [location.pathname, locationState?.hasLoanRequest, navigate]);

  // Categorize taken loans
  const {currentTakenLoans, paidTakenLoans} = useMemo(() => {
    const current: ITakenLoan[] = [];

    const paid: ITakenLoan[] = [];

    takenLoans.forEach(loan => {
      if (loan?.is_paid) {
        paid.push(loan);
      } else {
        current.push(loan);
      }
    });

    return {
      currentTakenLoans: current,
      paidTakenLoans: paid,
    };
  }, [takenLoans]);

  // Categorize loan requests and also add a sorting that the ones with incomplete status is at the top
  const currentLoanRequests = useMemo(() => {
    return [...loanRequests].sort((a, b) => {
      const aCompleted = isCompletedLoanRequest(a);
      const bCompleted = isCompletedLoanRequest(b);

      if (aCompleted === bCompleted) return 0;
      return aCompleted ? 1 : -1; // aCompleted ? move a after b : move a before b
    });
  }, [loanRequests]);

  const paidLoanRequests = useMemo(
    () => loanRequests.filter(isPaidLoanRequest),
    [loanRequests],
  );

  // Pending loan request
  const pendingLoanRequest = useMemo(
    () =>
      loanRequests.find(
        loan => loan?.status && loan?.status !== LoanRequestStatus.COMPLETED,
      ),
    [loanRequests],
  );

  // Empty states
  const isCurrentTabEmpty =
    currentLoanRequests.length === 0 &&
    currentTakenLoans.length === 0 &&
    currentCashBacks.length === 0;

  const isPaidTabEmpty =
    paidLoanRequests.length === 0 &&
    paidTakenLoans.length === 0 &&
    paidCashBacks.length === 0;

  // Tab change
  const handleTabChange = (tab: MyCreditsTabsType) => {
    setSelectedLoanGuid('');

    const params = new URLSearchParams(location.search);

    params.set('tab', tab);

    navigate(`${location.pathname}?${params.toString()}`, {
      replace: true,
    });
  };

  // Selected item
  const selectedLoan: CreditItem | null = useMemo(() => {
    if (!selectedLoanGuid) return null;

    const takenLoan =
      currentTakenLoans.find(loan => loan?.guid === selectedLoanGuid) ||
      paidTakenLoans.find(loan => loan?.guid === selectedLoanGuid);

    if (takenLoan) {
      return {
        type: 'taken-loan',
        data: takenLoan,
      };
    }

    const loanRequest = loanRequests.find(
      loan => loan?.guid === selectedLoanGuid,
    );

    if (loanRequest) {
      return {
        type: 'loan-request',
        data: loanRequest,
      };
    }

    const cashBack =
      allCashBacks?.find(cashback => cashback.guid === selectedLoanGuid) ??
      (DEFAULT_CASH_BACK.guid === selectedLoanGuid ? DEFAULT_CASH_BACK : null);

    if (cashBack) {
      return {
        type: 'cashback',
        data: cashBack,
      };
    }

    return null;
  }, [currentTakenLoans, paidTakenLoans, loanRequests, selectedLoanGuid]);

  const handleTakenLoanNavigation = () => {
    if (!selectedLoan) return;

    if (selectedLoan?.type === 'taken-loan' && selectedLoan?.data.is_paid) {
      navigate(`/loan-payment-details/${selectedLoan?.data.guid}/isPaid`);
      return;
    }

    const isSelectedLoanPaidInBank =
      selectedLoan?.type === 'loan-request' &&
      isCompletedLoanRequest(selectedLoan?.data) &&
      selectedLoan?.data?.is_direct === true;

    if (isSelectedLoanPaidInBank) {
      navigate(`/loan-payment-details/${selectedLoan?.data.guid}/isBank`);
      return;
    }

    const isSelectedCashBack = selectedLoan?.type === 'cashback';

    if (isSelectedCashBack) {
      const {guid} = selectedLoan?.data;

      if (!guid) return;

      if (guid === 'default') {
        setOpenDefaultCashBackSheet(true);
        return;
      }

      navigate(`/cash-back-details/${guid}`);
      return;
    }
  };

  const handlePendingLoanNavigation = () => {
    if (
      !selectedLoan ||
      selectedLoan?.type === 'taken-loan' ||
      selectedLoan?.type === 'cashback'
    )
      return;

    const nextRoute = navigateToStep(selectedLoan?.data);

    if (!nextRoute) return;

    navigate(nextRoute, {replace: true});
  };

  const isLoading =
    isLoanRequestsLoading || isTakenLoansLoading || isCashBackCardsLoading;

  useEffect(() => {
    const areBothTabsEmpty = isPaidTabEmpty && isCurrentTabEmpty;

    if (isLoading || !areBothTabsEmpty) return;

    navigate('/my-credits/startLoanCredit', {
      replace: true,
    });
  }, [isPaidTabEmpty, isCurrentTabEmpty, isLoading, navigate]);

  const showSelectedLoanAction = Boolean(selectedLoanGuid);

  const currentTabState = getCurrentTabState(
    currentLoanRequests,
    currentTakenLoans,
  );

  const allCurrentLoansLength =
    currentLoanRequests?.length +
    currentTakenLoans?.length +
    currentCashBacks?.length;

  useEffect(() => {
    if (
      currentTabState !== CurrentTabStateTypes.LoanRequestsOnly ||
      !pendingLoanRequest?.guid ||
      currentCashBacks?.length
    )
      return;

    setSelectedLoanGuid(pendingLoanRequest.guid);
  }, [pendingLoanRequest, currentTabState]);

  const HeaderText = getLoanPageHeader(currentTabState);

  const isLoanInSettlement =
    selectedLoan?.type === 'loan-request' &&
    selectedLoan?.data?.status === LoanRequestStatus.COMPLETED &&
    selectedLoan?.data?.is_direct &&
    !selectedLoan?.data?.is_paid_in_bank;

  const isTabEmpty =
    selectedTab === TAB_CURRENT ? isCurrentTabEmpty : isPaidTabEmpty;

  // Sections that are removed from the DOM while the list is scrolled down
  const showHeaderText =
    selectedTab === TAB_CURRENT &&
    !isCurrentTabEmpty &&
    !!HeaderText &&
    !isCollapsed;

  const showSelectHint =
    selectedTab === TAB_CURRENT &&
    !isCurrentTabEmpty &&
    allCurrentLoansLength > 1;

  if (isLoading) {
    return (
      <div className="grid h-full place-content-center">
        <PageLoader />
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white h-dvh">
      <CustomHeader
        sticky
        preComponent={() => (
          <div
            className="relative flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/my-account')}
          >
            <i className="icon-nameArrow---Right text-[25px] text-gray-700" />

            <span className="font-medium">اعتبار من</span>
          </div>
        )}
        postComponent={() => (
          <div className="flex gap-3">
            <NotificationHeaderIcon />

            <ChatHeaderIcon />
          </div>
        )}
      />

      {/* Section 1: tab selector */}
      {!isCollapsed && (
        <div className="sticky top-[48px] z-10 bg-white px-3 pt-3">
          <LoanTabSelector
            selectedTab={selectedTab}
            setSelectedTab={handleTabChange}
          />
        </div>
      )}

      {/* The list fills whatever space is left, so it grows when sections are removed */}
      <div
        className="flex flex-col flex-1 min-h-0 gap-6 px-6 pt-6"
        ref={containerRef}
      >
        {/* Section 2: header text */}
        {showHeaderText && (
          <span className="font-normal text-md text-zinc-600 shrink-0">
            {HeaderText}
          </span>
        )}

        <div
          ref={scrollRef}
          className={`flex-1 min-h-0 space-y-3 overflow-y-auto overscroll-contain pb-20 ${
            selectedTab === TAB_CURRENT
              ? 'shadow-[0_3px_4px_-2px_rgba(0,0,0,0.15)]'
              : ''
          }`}
        >
          {isTabEmpty && <NoItemFoundPage className="h-[30dvh]" />}

          {/* CURRENT TAB */}
          {selectedTab === TAB_CURRENT && !isCurrentTabEmpty && (
            <>
              {/* Loan requests */}
              {currentLoanRequests.map(loan => (
                <InProgressLoanCard
                  key={loan?.guid}
                  loan={loan}
                  isSelected={selectedLoanGuid === loan?.guid}
                  setSelectedLoanGuid={setSelectedLoanGuid}
                  currentTabState={currentTabState}
                  allCurrentLoansLength={allCurrentLoansLength}
                />
              ))}

              {/* Current CashBacks And Default CashBack*/}
              {currentCashBacks?.length > 0 ? (
                currentCashBacks.map((cashback: ICashBackCard) => (
                  <CashBackCard
                    key={cashback.guid}
                    cashback={cashback}
                    isSelected={selectedLoanGuid === cashback.guid}
                    setSelectedLoanGuid={setSelectedLoanGuid}
                  />
                ))
              ) : (
                <CashBackCard
                  cashback={DEFAULT_CASH_BACK}
                  isSelected={selectedLoanGuid === DEFAULT_CASH_BACK.guid}
                  setSelectedLoanGuid={setSelectedLoanGuid}
                />
              )}

              {/* Taken loans */}
              {currentTakenLoans.map(loan => (
                <TakenLoanCard
                  key={loan?.guid}
                  loan={loan}
                  isSelected={selectedLoanGuid === loan?.guid}
                  setSelectedLoanGuid={setSelectedLoanGuid}
                />
              ))}
            </>
          )}

          {/* PAID TAB */}
          {selectedTab === TAB_PAID && !isPaidTabEmpty && (
            <>
              {/* Paid CashBacks */}
              {paidCashBacks?.map((cashback: ICashBackCard) => (
                <CashBackCard
                  key={cashback.guid}
                  cashback={cashback}
                  isSelected={selectedLoanGuid === cashback.guid}
                  setSelectedLoanGuid={setSelectedLoanGuid}
                  tabType={selectedTab}
                />
              ))}

              {/* Paid requests */}
              {paidLoanRequests.map(loan => (
                <InProgressLoanCard
                  key={loan?.guid}
                  loan={loan}
                  isSelected={selectedLoanGuid === loan?.guid}
                  setSelectedLoanGuid={setSelectedLoanGuid}
                  currentTabState={currentTabState}
                  allCurrentLoansLength={allCurrentLoansLength}
                />
              ))}

              {/* Paid taken loans */}
              {paidTakenLoans.map(loan => (
                <TakenLoanCard
                  tabType={selectedTab}
                  key={loan?.guid}
                  loan={loan}
                  isSelected={selectedLoanGuid === loan?.guid}
                  setSelectedLoanGuid={setSelectedLoanGuid}
                />
              ))}
            </>
          )}
        </div>

        {showSelectHint && (
          <div className="flex items-center gap-2 text-zinc-500 shrink-0">
            <i className="text-lg icon-namereport" />
            <span className="text-sm">
              یکی از درخواست‌های خود را انتخاب کنید
            </span>
          </div>
        )}
      </div>

      {/* Section 3: bottom actions */}
      {!isCollapsed && (
        <StickySection hasShadow={false}>
          {selectedTab === 'current' &&
            (currentTabState === CurrentTabStateTypes.Both ||
              currentTabState === CurrentTabStateTypes.LoanRequestsOnly) && (
              <Button
                onClick={handlePendingLoanNavigation}
                disabled={
                  selectedLoan?.type !== 'loan-request' ||
                  !showSelectedLoanAction ||
                  isLoanInSettlement
                }
              >
                ادامه فرایند
              </Button>
            )}

          {selectedTab !== 'paid' && (
            <Button
              disabled={
                (selectedLoan?.type === 'loan-request' &&
                  !isLoanInSettlement) ||
                !showSelectedLoanAction
              }
              onClick={handleTakenLoanNavigation}
            >
              مشاهده جزئیات
            </Button>
          )}

          {selectedTab === 'current' &&
            (currentTabState === CurrentTabStateTypes.TakenLoansOnly ||
              currentTabState === CurrentTabStateTypes.Empty) && (
              <Button onClick={() => navigate('/my-credits/startLoanCredit')}>
                درخواست جدید
              </Button>
            )}
        </StickySection>
      )}

      <DefaultCashBackSheet
        open={openDefaultCashBackSheet}
        onClose={() => setOpenDefaultCashBackSheet(false)}
      />

      {/* New credit */}
      <MainFooter isScrolling={false} />
    </div>
  );
};

export default MyCreditsPage;
